import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { TrainingService } from '@/services/training.service';
import { TrainingStatus } from '@/models/training.model';

export const Training: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'courses' | 'assignments' | 'certifications'>('courses');

  const { data: courses = [], isLoading: coursesLoading } = useQuery({
    queryKey: ['trainingCourses'],
    queryFn: () => TrainingService.getAllCourses(),
    enabled: !!profile,
  });

  const { data: assignments = [], isLoading: assignmentsLoading } = useQuery({
    queryKey: ['trainingAssignments'],
    queryFn: () => TrainingService.getAllAssignments(),
    enabled: !!profile,
  });

  const completedCourses = assignments.filter(a => a.status === TrainingStatus.completed).length;
  const inProgress = assignments.filter(a => a.status === TrainingStatus.inProgress).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Training & Development</h1>
        <p className="mt-2 text-gray-600">Manage training courses, assignments, and certifications</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Courses</div>
          <div className="text-2xl font-bold text-gray-900">{courses.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">In Progress</div>
          <div className="text-2xl font-bold text-yellow-600">{inProgress}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Completed</div>
          <div className="text-2xl font-bold text-green-600">{completedCourses}</div>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'courses' as const, name: 'Courses', count: courses.length },
            { id: 'assignments' as const, name: 'Assignments', count: assignments.length },
            { id: 'certifications' as const, name: 'Certifications', count: 0 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name} {tab.count > 0 && <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">{tab.count}</span>}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'courses' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Training Courses</h2>
            <Link
              to="/training/courses/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Create Course
            </Link>
          </div>
          {coursesLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : courses.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No courses found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {courses.map((course) => (
                  <li key={course.id}>
                    <Link
                      to={`/training/courses/${course.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{course.title}</p>
                            {course.isRequired && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {course.description} • {course.duration} minutes • {course.type}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === 'assignments' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Assignments</h2>
            <Link
              to="/training/assignments/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Assign Course
            </Link>
          </div>

          {assignmentsLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : assignments.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No assignments found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {assignments.map((a) => (
                  <li key={a.id} className="px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{a.courseTitle}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {a.employeeName} • Status: {a.status.split('_').join(' ')}
                          {a.dueDate ? ` • Due ${a.dueDate.toLocaleDateString()}` : ''}
                        </p>
                        {a.notes && <p className="mt-1 text-xs text-gray-500 whitespace-pre-wrap">{a.notes}</p>}
                      </div>
                      <div className="text-xs text-gray-500">
                        Assigned {a.assignedDate.toLocaleDateString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === 'certifications' && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">Certifications management coming next.</p>
        </div>
      )}
    </div>
  );
};

